import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { StatusBadge, EmptyState } from "@/components/ui/bits";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "overdue", label: "Overdue" },
  { key: "awaiting_client", label: "Awaiting client" },
  { key: "to_review", label: "To review" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
];

const STATUS_OPTS = ["upcoming", "awaiting_client", "to_review", "completed"];

export default function DeadlinesPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [filter, setFilter] = useState(params.get("status") || "all");

  const load = async () => {
    try {
      const { data } = await api.get("/deadlines");
      setDeadlines(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/deadlines/${id}`, { status });
      setDeadlines((ds) => ds.map((d) => (d.id === id ? { ...d, status, is_overdue: status !== "completed" && d.is_overdue } : d)));
      toast.success("Updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = deadlines.filter((d) => {
    if (filter === "all") return true;
    if (filter === "overdue") return d.is_overdue;
    return d.status === filter;
  });

  return (
    <div>
      <h1 className="font-serif font-bold text-3xl text-white mb-1">Deadline tracker</h1>
      <p className="text-slate-400 mb-5">Statutory filings across your clients.</p>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border transition-colors ${
              filter === f.key ? "bg-[#C9A84A] text-[#0A0F1F] border-[#C9A84A] font-semibold" : "card-surface text-slate-300 border-[#1E294B]"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No deadlines here" hint="They will appear as clients are onboarded." />
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => (
            <div key={d.id} className="card-surface rounded-2xl p-4 flex items-center justify-between gap-4" data-testid="deadline-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium truncate">{d.title}</span>
                  <StatusBadge status={d.status} overdue={d.is_overdue} />
                </div>
                <button onClick={() => navigate(`/clients/${d.client_id}`)} className="text-[#E5C158] text-sm hover:underline">{d.client_name}</button>
                <span className="text-slate-500 text-sm"> · Due {fmtDate(d.due_date)}</span>
              </div>
              <select value={d.status} onChange={(e) => changeStatus(d.id, e.target.value)} data-testid="deadline-status-select"
                className="bg-[#0A0F1F] border border-[#1E294B] rounded-lg px-2 py-2 text-sm text-white outline-none shrink-0">
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
